import { FC, useEffect, useState } from 'react';

import { GiftCouponSummary } from '@shared/api/giftCoupon/giftCoupon.interface';

import { CouponTable } from './CouponTable';
import { CrossCouponHashOverlapResult } from './kakaoShareOverlap';
import { GiftItemEmbed } from './types';

const chatTypeLabel: Record<string, string> = {
  MemoChat: '나에게 보내기',
  DirectChat: '1:1 채팅',
  MultiChat: '단톡방',
  OpenMultiChat: '오픈 단톡방',
};

interface CrossCouponOverlapBannerProps {
  overlap: CrossCouponHashOverlapResult;
  /** 쿠폰 ID → 쿠폰 객체 (그룹별 행 렌더링용) */
  couponMap: Map<string, GiftCouponSummary>;
  /** 쿠폰 → 상품 임베드 정보 (CouponRow가 사용) */
  giftItemEmbedMap: Map<string, GiftItemEmbed>;
  /** 카톡공유기록 칩 클릭 → 콜백 모달 */
  onOpenKakaoShareCallbacks?: (coupon: GiftCouponSummary) => void;
  /** 재공유이력 칩 클릭 → 재공유 이력 모달 */
  onOpenShareHistory: (coupon: GiftCouponSummary) => void;
}

/**
 * "다건수신 (주문내)" 위험 배너.
 *
 * - 정의: 같은 주문 안의 서로 다른 쿠폰이 동일한 카카오톡 채팅방으로 공유된 케이스
 *   = 한 카톡방이 같은 주문의 여러 쿠폰을 함께 수신.
 * - 펼치면 그룹별로 해당 쿠폰들을 본 테이블과 동일한 행 형태로 보여줌
 *   (구매일/만료·교환일 / 환불·연장 액션은 숨김).
 */
export const CrossCouponOverlapBanner: FC<CrossCouponOverlapBannerProps> = ({
  overlap,
  couponMap,
  giftItemEmbedMap,
  onOpenKakaoShareCallbacks,
  onOpenShareHistory,
}) => {
  // 기본 접힘 — 위험 요약 헤더만 보이고, 상세 그룹은 클릭해서 펼친다
  const [expanded, setExpanded] = useState(false);

  // [DEBUG] 다건수신(주문내) — 그룹/쿠폰 데이터 진단용 콘솔 로그
  useEffect(() => {
    if (overlap.groups.length === 0) return;
    /* eslint-disable no-console */
    console.groupCollapsed(
      `[CrossCouponOverlapBanner] 다건수신(주문내) ${overlap.groups.length}개 채팅방 / 쿠폰 ${overlap.overlapCouponIds.size}개`,
    );
    overlap.groups.forEach((g, idx) => {
      console.log(`[group #${idx + 1}] hashChatId=${g.hashChatId}`, {
        chatType: g.chatType,
        couponIds: g.couponIds,
        callbacksLength: g.callbacks.length,
      });
    });
    console.groupEnd();
    /* eslint-enable no-console */
  }, [overlap]);

  if (overlap.groups.length === 0) return null;

  const totalCoupons = overlap.overlapCouponIds.size;
  const totalGroups = overlap.groups.length;

  return (
    <div className="mb-2 shrink-0 rounded-lg border border-red-300 bg-red-50">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-t-lg px-3 py-2 text-left hover:bg-red-100/60"
      >
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white">
            ⚠ 다건수신 (주문내)
          </span>
          <p className="text-xs font-semibold text-red-800">
            서로 다른 {totalCoupons}개 쿠폰이 같은 카톡방으로 수신됨
            <span className="ml-1 font-normal text-red-600">
              · {totalGroups}개 채팅방
            </span>
          </p>
        </div>
        <svg
          className={`h-3.5 w-3.5 text-red-700 transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {expanded && (
        <div className="max-h-[50vh] space-y-2 overflow-y-auto rounded-b-lg border-t border-red-200 px-3 py-2">
          {overlap.groups.map((g, idx) => {
            const typeLabel = chatTypeLabel[g.chatType] ?? g.chatType ?? '알 수 없음';
            const coupons = g.couponIds
              .map((id) => couponMap.get(id))
              .filter((c): c is GiftCouponSummary => Boolean(c))
              .sort((a, b) => a.seq - b.seq);
            return (
              <div key={g.key} className="rounded-md border border-red-200 bg-white">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-red-100 bg-red-50/60 px-2.5 py-1.5">
                  <span className="text-[10px] font-semibold text-red-500">
                    #{idx + 1}
                  </span>
                  <span className="inline-flex rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">
                    {coupons.length}개 쿠폰
                  </span>
                  <span className="inline-flex rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700">
                    {typeLabel}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    카톡방 hashId{' '}
                    <span className="font-mono text-gray-600" title={g.hashChatId}>
                      {g.hashChatId}
                    </span>
                  </span>
                </div>
                {coupons.length === 0 ? (
                  <p className="px-3 py-2 text-[11px] italic text-red-400">
                    (쿠폰 객체 매칭 실패 — 콘솔 로그 확인)
                  </p>
                ) : (
                  <CouponTable
                    sortedCoupons={coupons}
                    giftItemEmbedMap={giftItemEmbedMap}
                    canCancel={false}
                    hasCancellableCoupons={false}
                    disposeMode={false}
                    selectedIds={new Set()}
                    disposeSelectedIds={new Set()}
                    isPromoting={false}
                    crossCouponOverlapIds={overlap.overlapCouponIds}
                    hideDates
                    toggleSelect={NOOP_STR}
                    toggleDisposeSelect={NOOP_STR}
                    onOpenShareHistory={onOpenShareHistory}
                    handlePromoteToReissued={NOOP_PROMISE}
                    onOpenKakaoShareCallbacks={onOpenKakaoShareCallbacks}
                    isIssuing={false}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const NOOP_STR = (_id: string): void => {
  void _id;
};
const NOOP_PROMISE = async (_id: string): Promise<void> => {
  void _id;
};
